import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'src/components/atoms/Button';
import TableImage from 'src/components/atoms/TableImage';
import Page from 'src/components/layouts/Page';
import InputWrap from 'src/components/molecules/InputWrap';
import PageButtonsBar from 'src/components/organisms/PageButtonsBar';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CropModal from 'src/components/modals/CropModal';
import Croppedimages from 'src/components/organisms/Croppedimages';
import { getItems, updateFilter } from 'src/slices/item';
import { addOffer, clearState, getOfferDetails, updateOffer } from 'src/slices/offer';
import css from './OfferDetails.module.scss';
import ImagePlaceholder from 'src/components/atoms/ImagePlaceholder';
import TableFilterable from 'src/components/organisms/TableFilterable';
import { handleStatus } from 'src/slices/utils';
import { DateTimePicker } from '@mantine/dates';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import ButtonGroup from 'src/components/atoms/ButtonGroup';
import { useDebouncer } from 'src/utils/debounce';
import { useIsMobile } from 'src/utils/UseMediaQuery';
dayjs.extend(dayOfYear);

export default function OfferDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const offerId = useParams().offer;
  const { offerDetails, status } = useSelector((state) => state.offer);

  const filter = useSelector((state) => state.item.filters.items);
  const { items: itemsSearch, displayShowMore } = useSelector((state) => state.item);
  const getItemsIndicator = useSelector((state) => state.item);
  const { permissions } = useSelector((state) => state.auth);

  const [showSearch, setShowSearch] = useState(false);

  const [page, setPage] = useState(1);

  const [offerInfo, setOfferInfo] = useState({
    items: [],
    qtyMap: {},

    name: '',
    description: '',
    qty: 1,
    discount: 15,

    startDate: dayjs().toDate(),
    endDate: dayjs().add(10, 'days').toDate(),

    sellBy: dayjs().add(10, 'days').toDate(),
    expiresOn: dayjs().add(15, 'days').toDate(),

    repeat: 'none',
    photos: [],
  });

  const updateForm = (field, data) => {
    setOfferInfo({ ...offerInfo, [field]: data });
  };

  useEffect(() => {
    if (offerId !== 'new') {
      dispatch(getOfferDetails(offerId));
    }

    dispatch(updateFilter({ items: { keyword: '' } }));
  }, [offerId, dispatch]);

  const initPage = useCallback(() => {
    setOfferInfo({
      items: offerDetails.items,
      qtyMap: offerDetails.qtyMap,

      name: offerDetails.name,
      description: offerDetails.description,
      qty: offerDetails.qty,
      discount: offerDetails.discount * 100,

      startDate: dayjs(offerDetails.startDate).toDate(),
      endDate: dayjs(offerDetails.endDate).toDate(),

      sellBy: dayjs(offerDetails.sellBy).toDate(),
      expiresOn: dayjs(offerDetails.expiresOn).toDate(),

      repeat: offerDetails.repeat,
      photos: offerDetails.photos,
    });
  }, [offerDetails]);

  useEffect(() => {
    if (offerId !== 'new') {
      initPage();
    }
  }, [offerDetails, initPage, offerId]);

  useEffect(() => {
    dispatch(getItems());
  }, [filter, dispatch]);

  useEffect(() => {
    handleStatus(status.addOffer, () => dispatch(clearState(status.addOffer)), {
      isSuccess: {
        callback: () =>
          permissions.create.includes('offers') ? navigate('/offers') : navigate('/pending-offers'),
      },
    });

    handleStatus(status.updateOffer, () => dispatch(clearState(status.updateOffer)), {
      isSuccess: {
        callback: () =>
          offerDetails.status !== 'pending' ? navigate('/offers') : navigate('/pending-offers'),
      },
    });
  }, [status, dispatch, navigate, offerDetails.status, permissions.create]);

  const calculatePrice = (discount = 0) => {
    if (!offerInfo.items) return 0;

    const total = offerInfo.items.reduce((acc, curr) => {
      return (acc = acc + curr.price * offerInfo.qtyMap[curr.id]);
    }, 0);

    return total * ((100 - discount) / 100);
  };

  const validator = (offer) => {
    if (!offer.name) {
      toast.warning(`"Name" is a required field`);
      return null;
    } else if (!offer.description) {
      toast.warning(`"Description" is a required field`);
      return null;
    } else if (!offer.startDate) {
      toast.warning(`"Start Date" is a required field`);
      return null;
    } else if (!offer.endDate) {
      toast.warning(`"End Date" is a required field`);
      return null;
    } else if (dayjs(offer.endDate).isBefore(dayjs(offer.startDate))) {
      toast.warning(
        `"Start Date" (${dayjs(offer.startDate).format('M/D')}) must be before "End Date" (${dayjs(
          offer.endDate,
        ).format('M/D')})`,
      );
      return null;
    } else if (!offer.qty) {
      toast.warning(`"Offer Quantity" is a required field`);
      return null;
    } else if (!offer.discount) {
      toast.warning(`"Discount" is a required field`);
      return null;
    } else if (offer.discount < 25 || offer.discount > 75) {
      toast.warning(`"Discount" must be between 25% and 75%`);
    } else if (
      offer.discount !== 25 &&
      offer.discount !== 40 &&
      offer.discount !== 50 &&
      offer.discount !== 65 &&
      offer.discount !== 75
    ) {
      toast.warning(`"Discount" must be chosen from the following list: 25%, 40%, 50%, 65%, 75%`);
      return null;
    } else if (offer.items?.length === 0) {
      toast.warning('Offers must contain at least one item');
      return null;
    } else {
      return offer;
    }
  };

  const handleSearch = (keyword) => {
    setShowSearch(true);
    dispatch(updateFilter({ items: { keyword: keyword, page: 1, noHidden: true } }));
    setPage(1);
  };

  useEffect(() => {
    dispatch(updateFilter({ items: { keyword: '', page: 1, skip: 200, noHidden: true } }));
  }, [dispatch]);

  const handleMore = () => {
    dispatch(
      updateFilter({ items: { limit: (page + 1) * filter.skip, page: page + 1, noHidden: true } }),
    );
    setPage(page + 1);
  };

  const debouncedSearch = useDebouncer(handleSearch);

  const handleAddItem = (item) => {
    setShowSearch(false);

    setOfferInfo({
      ...offerInfo,

      ...(offerId === 'new' &&
        offerInfo.items.length === 0 && {
          name: item.name,
          description: item.description,
        }),
      ...(offerId === 'new' &&
        offerInfo.items.length === 1 && {
          name: '',
          description: '',
        }),
      photos: [...offerInfo.photos, ...item.photos],
      items: [...offerInfo.items, item],
      qtyMap: { ...offerInfo.qtyMap, [item.id]: 1 },
    });

    dispatch(updateFilter({ items: { keyword: '' } }));
  };

  const handleRemoveItem = useCallback(
    (item, index) => {
      setOfferInfo({
        ...offerInfo,
        ...(offerId === 'new' &&
          offerInfo.items.length === 1 && {
            name: '',
            description: '',
          }),
        ...(offerId === 'new' &&
          offerInfo.items.length === 2 && {
            name: offerInfo.items[0].name,
            description: offerInfo.items[0].description,
          }),
        items: offerInfo.items.filter((x, i) => index !== i),
        photos: offerInfo.photos.filter((photo, i) => !item.photos.includes(photo)),
      });
    },
    [offerId, offerInfo],
  );

  const handleIncrease = useCallback(
    (item) => {
      const temp = { ...offerInfo.qtyMap };
      temp[item.id] += 1;
      setOfferInfo({ ...offerInfo, qtyMap: temp });
    },
    [offerInfo],
  );

  const handleDecrease = useCallback(
    (item) => {
      if (offerInfo.qtyMap[item.id] > 1) {
        setOfferInfo({
          ...offerInfo,
          qtyMap: {
            ...offerInfo.qtyMap,
            [item.id]: offerInfo.qtyMap[item.id] - 1,
          },
        });
      } else {
        setOfferInfo({
          ...offerInfo,
          items: offerInfo.items.filter((currItem) => item.id !== currItem.id),
          photos: offerInfo.photos.filter((photo, i) => !item.photos.includes(photo)),
        });
      }
    },
    [offerInfo],
  );

  const handleSave = () => {
    if (validator(offerInfo)) {
      const data = {
        ...offerInfo,
        items: JSON.stringify(offerInfo.items.map((item) => item.id)),
        qtyMap: JSON.stringify(offerInfo.qtyMap),
        photos: offerInfo.photos,
        id: offerId,
      };

      offerId === 'new' ? dispatch(addOffer(data)) : dispatch(updateOffer(data));
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Image',
        accessor: 'photos',
        width: '80px',
        disableSortBy: true,
        Cell: (props) => (
          <TableImage
            iconUnicode=""
            src={props?.cell?.row?.values?.photos?.[0]}
            type="rect"
            alt={props?.cell?.row?.values?.name}
          />
        ),
      },
      {
        Header: 'Name',
        accessor: 'name',
        width: 'auto',
        // accessor: row => `$${row?.price}`,
      },
      {
        Header: 'Price',
        width: 'auto',
        accessor: (row) => `$${row?.price.toFixed(2)}`,
      },
      {
        Header: 'Total Price',
        width: 'auto',
        accessor: (row) => `$${(row?.price * offerInfo.qtyMap[row.id]).toFixed(2)}`,
      },
      {
        Header: 'Quantity',
        width: 'auto',
        accessor: 'quantity',
        disableSortBy: true,
        Cell: (props) => (
          <div>
            <div className={css['quantity-selector']}>
              <Button
                iconLeft="fa fa-minus"
                color="red"
                variant="secondary"
                size="s"
                action={() => {
                  handleDecrease(props?.cell?.row?.original);
                }}
              />
              <input
                value={offerInfo.qtyMap[props?.cell?.row?.original?.id]}
                onChange={(e) => {
                  setOfferInfo({
                    ...offerInfo,
                    qtyMap: {
                      ...offerInfo.qtyMap,
                      [props?.cell?.row?.values?.id]: e.target.value,
                    },
                  });
                }}
              />
              <Button
                iconLeft="fa fa-plus"
                color="emerald"
                variant="secondary"
                // variant="tertiary"
                size="s"
                action={() => {
                  handleIncrease(props?.cell?.row?.original);
                }}
              />
            </div>
          </div>
        ),
      },
      {
        // Header: "Actions",
        Header: ' ',
        width: '24px',
        Cell: (props) => (
          <div className={css['table-actions']}>
            <Button
              iconLeft="fad fa-times"
              color="gray"
              variant="secondary"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
              }}
              action={() => handleRemoveItem(props?.cell?.row?.original, props?.cell?.row?.index)}
            />
          </div>
        ),
      },
    ],
    [offerInfo, handleIncrease, handleDecrease, handleRemoveItem],
  );

  const handleNavigate = () => {
    navigate('/offers');
  };

  /*
	Image Uploading
	*/
  const [croppedImage, setCroppedImage] = useState();
  const [showModal, setShowModal] = useState(false);

  const addImage = (photo) => {
    updateForm('photos', [...offerInfo.photos, photo]);
  };

  const removeImage = (photo) => {
    updateForm(
      'photos',
      offerInfo.photos.filter((currPhoto) => currPhoto !== photo),
    );
  };

  const handleCropped = (photo) => {
    updateForm('photos', [...offerInfo.photos, photo]);
    setShowModal(false);
  };

  const closeFunction = () => {
    setShowModal(false);
  };

  useEffect(() => {
    const callback = () => setShowSearch(false);

    window.addEventListener('pointerdown', callback);

    return () => window.removeEventListener('pointerdown', callback);
  }, []);

  const isMobile = useIsMobile()

  return (
    <Page>
      <PageButtonsBar
        buttons={
          <>
            <Button
              size={isMobile ? "s" : "m"}
              label="Cancel"
              color="gray"
              variant="tertiary"
              action={handleNavigate}
            />
            <Button
              size={isMobile ? "s" : "m"}
              color="error"
              variant="tertiary"
              style={{ marginRight: 'auto' }}
              label={offerId === 'new' ? 'Restart' : 'Discard'}
              action={() => initPage()}
              iconLeft={offerId === 'new' ? 'fa fa-undo' : 'fas fa-trash-alt'}
            />
            <Button
              size={isMobile ? "s" : "m"}
              label={
                offerId === 'new'
                  ? `Create ${!permissions.create.includes('offers') ? 'Pending' : ''} Offer`
                  : 'Save'
              }
              action={handleSave}
              style={{ marginLeft: 'auto' }} // for small screens
              color={'emerald'}
              variant={'primary'}
              state={'null'}
            />
          </>
        }
      />

      <div className={css.layout}>
        <div className={css['layout-section']}>
          <h2 className={css['col-title']}>Offer Items</h2>
          <div onPointerDown={(e) => e.stopPropagation()} className={css['search']}>
            <InputWrap
              component={
                <input
                  placeholder={`Search for items to add to the offer...`}
                  onChange={(e) => debouncedSearch(e.target.value)}
                  onFocus={(e) => debouncedSearch(e.target.value)}
                  className={css['search-input']}
                />
              }
            />

            {/* {showSearch && ( */}
            <div className={`${css['results']} ${showSearch ? css['results-open'] : ''}`}>
              {getItemsIndicator.status.getItems.isLoading && page <= 1 ? (
                <p className={css.notfound}>
                  <i className="fad fa-spinner fa-spin" /> Loading...
                </p>
              ) : itemsSearch.length > 0 ? (
                <>
                  {itemsSearch
                    ?.filter((item) => !offerInfo?.items?.some((x) => x._id === item._id))
                    .map((item) => {
                      return (
                        <div className={css['result']} key={item.id}>
                          <ImagePlaceholder
                            src={item.photos?.[0]}
                            size="48px"
                            style={{
                              borderRadius: '8px',
                            }}
                            iconName="fad fa-tag"
                          />
                          <p>{item.name}</p>
                          <p>${parseFloat(item.price).toFixed(2)}</p>
                          <Button
                            label="Add"
                            action={() => handleAddItem(item)}
                            iconLeft="fa fa-plus"
                            color="emerald"
                            variant="tertiary"
                            size="s"
                          />
                        </div>
                      );
                    })}
                  {displayShowMore && (
                    <div className={css.showMoreContainer}>
                      <Button
                        label={
                          getItemsIndicator.status.getItems.isLoading && page > 1
                            ? 'Loading...'
                            : 'Show More'
                        }
                        action={handleMore}
                      />
                    </div>
                  )}
                </>
              ) : (
                <p className={css.notfound}>No items found</p>
              )}
            </div>
            {/* )} */}
          </div>
          {/* maybe replace this with a simpler component. Do we really need sorting and stuff? */}
          {offerInfo.items && (
            <TableFilterable
              columns={columns}
              data={offerInfo.items}
              showBar={false}
              cellPadding="8px"
            />
          )}
        </div>
        <div className={css['layout-section']}>
          {/* what is this? */}
          <div>
            <h2 className={css['col-title']}>Offer Details</h2>
            <div className={css['left']}>
              <div className={css['final-price']}>
                <p>
                  {`Discounted Price: $${
                    offerInfo.price?.toFixed(2) || calculatePrice(offerInfo.discount).toFixed(2)
                  }`}{' '}
                  (
                  {`$${offerInfo.price?.toFixed(2) || calculatePrice().toFixed(2)} with ${
                    offerInfo.discount || 0
                  }% Discount`}
                  )
                </p>
              </div>
              <InputWrap
                label="name"
                component={
                  <input
                    style={{ width: '100%' }}
                    value={offerInfo.name}
                    onChange={(e) => updateForm('name', e.target.value)}
                    placeholder={`e.g. "Surf and Turf Special"`}
                  />
                }
              />

              <InputWrap
                label="Description"
                component={
                  <textarea
                    value={offerInfo.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Add a brief, but interesting description of your offer here"
                  />
                }
              />

              <div className={css['time-input']}>
                <DateTimePicker
                  placeholder="Pick date"
                  label="Start date"
                  value={offerInfo.startDate}
                  onChange={(value) => updateForm('startDate', dayjs(value).toDate())}
                  required
                  clearable={false}
                  error={dayjs(offerInfo.endDate).isBefore(dayjs(offerInfo.startDate))}
                />

                <DateTimePicker
                  placeholder="Pick date"
                  label="End date"
                  value={offerInfo.endDate}
                  onChange={(value) => updateForm('endDate', dayjs(value).toDate())}
                  required
                  clearable={false}
                  error={dayjs(offerInfo.endDate).isBefore(dayjs(offerInfo.startDate))}
                />
              </div>

              <div className={css['half-input']}>
                <InputWrap
                  label="Offer Quantity"
                  component={
                    <input
                      type="number"
                      min="0"
                      value={offerInfo.qty}
                      onChange={(e) => updateForm('qty', e.target.value)}
                    />
                  }
                />
                <InputWrap
                  label="Discount (%)"
                  component={
                    <ButtonGroup
                      setOption={(e) => updateForm('discount', e)}
                      selectedOption={offerInfo.discount}
                      options={[
                        { value: 25, label: '25%' },
                        { value: 40, label: '40%' },
                        { value: 50, label: '50%' },
                        { value: 65, label: '65%' },
                        { value: 75, label: '75%' },
                      ]}
                    />
                  }
                />
              </div>

              <Croppedimages
                images={offerInfo.photos}
                addImage={addImage}
                removeImage={removeImage}
              />
              <div className={css['image-buttons']}>
                {/* <Pill label='Clear Photos' color={'gray'} variant='secondary' action={} /> */}
                <Button
                  label="Undo"
                  action={() => updateForm('photos', JSON.parse(offerDetails.photos))}
                  color="normal"
                  variant="secondary"
                  iconLeft="fas fa-undo"
                />

                <Button
                  action={() => updateForm('photos', [])}
                  color="error"
                  variant="tertiary"
                  // style={{ width: '$grid-size * 20' }}
                  label="Remove Images"
                  iconLeft="fa fa-trash-alt "
                />
              </div>

              {/*  <div className={css["half-input"]}>
                                <InputWrap
                                    label="Categories"
                                    component={<Select isDisabled={true} />}
                                    helpText="Categories are provided by Livecart™"
                                />
                                <InputWrap
                                    label="Tags"
                                    component={<Select isDisabled={true} />}
                                />
                            </div> */}
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <CropModal
          closeFunction={closeFunction}
          image={croppedImage}
          hadnlecropped={handleCropped}
        />
      )}
    </Page>
  );
}
